import { memo } from "react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ToggleLevel {
  value: string;
  numeric: number;
  icon: LucideIcon;
}

interface ParameterToggleProps {
  label: string;
  levels: [ToggleLevel, ToggleLevel, ToggleLevel];
  currentValue: number;
  onChange: (value: number) => void;
}

function closestLevel(levels: ToggleLevel[], value: number): string {
  let closest = levels[0];
  let minDist = Math.abs(value - closest.numeric);
  for (const level of levels) {
    const dist = Math.abs(value - level.numeric);
    if (dist < minDist) {
      closest = level;
      minDist = dist;
    }
  }
  return closest.value;
}

export const ParameterToggle = memo(function ParameterToggle({
  label,
  levels,
  currentValue,
  onChange,
}: ParameterToggleProps) {
  const active = closestLevel(levels, currentValue);

  return (
    <div className="flex flex-col items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      <ToggleGroup
        type="single"
        value={active}
        onValueChange={(val) => {
          if (!val) return;
          const level = levels.find((l) => l.value === val);
          if (level) onChange(level.numeric);
        }}
        className="bg-white/5 rounded-lg p-0.5"
      >
        {levels.map((level) => {
          const Icon = level.icon;
          return (
            <ToggleGroupItem
              key={level.value}
              value={level.value}
              className={cn(
                "h-8 w-8 rounded-md data-[state=on]:bg-white/15 data-[state=on]:text-foreground",
                "text-muted-foreground hover:text-foreground transition-all"
              )}
              title={level.value}
            >
              <Icon className="w-4 h-4" />
            </ToggleGroupItem>
          );
        })}
      </ToggleGroup>
    </div>
  );
});
